// A four-sided shape with its diagonals (Year 8, 2 Nov 2026). A
// `quad_diagonals` is one shape with 4 straight sides and one or both of its
// diagonals drawn. Its corners and the point where the diagonals cross can
// carry letters. Sides and the parts of the diagonals can carry small marks
// (1 to 3) and labels: a length ("10 cm"), a letter, an expression
// ("2x + 3") or "?". Sides can carry arrows (1 to 3). Angles at the corners
// and at the crossing can carry arcs, a right-angle square and a label.
//
// The screen reader never names the shape (its name is often the answer):
// "A shape with 4 straight sides, corners A, B, C, D going clockwise from
// the top left. Its diagonals AC and BD cross at M." Then the labels and
// marks, part by part. Arrows are read as arrows, never as "parallel", and
// marks are counted, never called "equal" (wording approved by the owner,
// 2 Nov 2026).
//
// The `shape` field only chooses the drawing (a rhombus or kite is drawn
// standing on a corner, the others with a flat top). The drawing follows
// the lengths and angle sizes written on it where it can, and always keeps
// its marks: parts with the same marks are drawn the same length, sides
// with the same arrows are drawn parallel and right angles are drawn
// square. Where the numbers cannot all be drawn true the picture is labelled
// "not drawn to scale"; numbers or marks the shape cannot keep are refused.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

import {
  CORNER_LETTER,
  GAP,
  LABEL_HEIGHT,
  crosses,
  distanceToLine,
  labelWidth,
  overlaps,
  NOT_TO_SCALE,
  PLACES,
  RADIANS,
  angleAt,
  arcsAt,
  bestFit,
  boundsOf,
  between,
  checkCost,
  clearestAway,
  cornerLabel,
  degreesOf,
  distance,
  firstClear,
  groupsOf,
  isAngleLabel,
  isPartLabel,
  joinNames,
  judge,
  labelWords,
  letterOutside,
  marksWords,
  outwardOf,
  readCountList,
  readLabelList,
  sidesOf,
  spokenList,
  spotsBeside,
  squareAt,
  ticksOn,
  turning,
  unit,
  along,
  writtenLengths,
  type Bounds,
  type Check,
  type FitRange,
  type PairArc,
  type PairLabel,
  type PairLine,
  type PairPoint,
  type Verdict,
} from './practice-shape-pair-data';

const QUAD_SHAPES = [
  'square',
  'rectangle',
  'parallelogram',
  'rhombus',
  'kite',
  'trapezium',
  'quadrilateral',
] as const;

export type QuadShape = (typeof QUAD_SHAPES)[number];

// Points are numbered: the corners 0 to 3 (A, B, C, D clockwise from the
// top left, or from the top for a shape standing on a corner), then 4 for
// the point where the diagonals cross.
const CROSSING = 4;

export type QuadAngle = {
  // The angle's name as written, such as "ABM".
  name: string;
  vertex: number;
  arms: [number, number];
  label: string;
  arcs: number;
  right: boolean;
};

export type QuadDiagonalsSpec = {
  type: 'quad_diagonals';
  shape: QuadShape;
  // The corner letters, or all "".
  corners: string[];
  // 'first' is the diagonal from the first corner (AC), 'second' the one
  // from the second corner (BD).
  diagonals: 'both' | 'first' | 'second';
  // The letter where the diagonals cross, or "".
  centre: string;
  // Sides AB, BC, CD, DA.
  sides: string[];
  sideMarks: number[];
  sideArrows: number[];
  // Diagonals AC, BD.
  diagonalLabels: string[];
  // The parts of the diagonals: AM, MC, BM, MD.
  halves: string[];
  halfMarks: number[];
  angles: QuadAngle[];
  // The angle where the diagonals cross is marked as a right angle.
  rightAtCentre: boolean;
  notToScale: boolean;
};

const MAX_ANGLES = 8;

// Which way round the corner letters are said to start.
function startsAtTop(shape: QuadShape): boolean {
  return shape === 'rhombus' || shape === 'kite';
}

// The usual outline of each shape, from its fitted values, in units where
// the shape is about 1 wide; y goes down the page.
function outline(shape: QuadShape, values: number[]): PairPoint[] | null {
  const [a = 0, b = 0, c = 0] = values;
  let points: PairPoint[];
  switch (shape) {
    case 'square':
      points = [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
      ];
      break;
    case 'rectangle':
      points = [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: a },
        { x: 0, y: a },
      ];
      break;
    case 'parallelogram':
      // a: height, b: how far the top is moved to the right.
      points = [
        { x: b, y: 0 },
        { x: b + 1, y: 0 },
        { x: 1, y: a },
        { x: 0, y: a },
      ];
      break;
    case 'rhombus':
      // a: half the upright diagonal, for half the diagonal across of 1.
      points = [
        { x: 0, y: -a },
        { x: 1, y: 0 },
        { x: 0, y: a },
        { x: -1, y: 0 },
      ];
      break;
    case 'kite':
      // a: from the crossing up to the top corner; b: down to the bottom.
      points = [
        { x: 0, y: -a },
        { x: 1, y: 0 },
        { x: 0, y: b },
        { x: -1, y: 0 },
      ];
      break;
    case 'trapezium':
      // a: where the top starts; b: its length; c: the height.
      points = [
        { x: a, y: 0 },
        { x: a + b, y: 0 },
        { x: 1, y: c },
        { x: 0, y: c },
      ];
      break;
    default:
      points = [
        { x: 0.04, y: 0 },
        { x: 1, y: 0.12 },
        { x: 0.84, y: 0.74 },
        { x: 0, y: 0.6 },
      ];
  }
  if (turning(points) <= 0) return null;
  return points;
}

function fitRanges(shape: QuadShape): FitRange[] {
  switch (shape) {
    case 'rectangle':
      return [{ low: 0.3, high: 2.5, start: 0.62 }];
    case 'parallelogram':
      return [
        { low: 0.3, high: 1.3, start: 0.8 },
        { low: -0.8, high: 0.8, start: 0.32 },
      ];
    case 'rhombus':
      return [{ low: 0.3, high: 2.5, start: 0.62 }];
    case 'kite':
      return [
        { low: 0.2, high: 1.6, start: 0.55 },
        { low: 0.4, high: 3, start: 1.3 },
      ];
    case 'trapezium':
      return [
        { low: -0.2, high: 0.6, start: 0.22 },
        { low: 0.25, high: 0.95, start: 0.55 },
        { low: 0.3, high: 1, start: 0.55 },
      ];
    default:
      return [];
  }
}

// Where the diagonals AC and BD cross.
function crossing(points: PairPoint[]): PairPoint {
  const [a, b, c, d] = points;
  if (!a || !b || !c || !d) return { x: 0, y: 0 };
  const r = { x: c.x - a.x, y: c.y - a.y };
  const s = { x: d.x - b.x, y: d.y - b.y };
  const across = r.x * s.y - r.y * s.x;
  if (Math.abs(across) < 1e-12) return a;
  const t = ((b.x - a.x) * s.y - (b.y - a.y) * s.x) / across;
  return { x: a.x + r.x * t, y: a.y + r.y * t };
}

function withCrossing(points: PairPoint[]): PairPoint[] {
  return [...points, crossing(points)];
}

// The parts measured: sides AB, BC, CD, DA; diagonals AC, BD; halves AM,
// MC, BM, MD.
const PARTS: [number, number][] = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 0],
  [0, 2],
  [1, 3],
  [0, CROSSING],
  [CROSSING, 2],
  [1, CROSSING],
  [CROSSING, 3],
];

function partsOf(points: PairPoint[]): number[] {
  return PARTS.map(([from, to]) =>
    distance(points[from] ?? { x: 0, y: 0 }, points[to] ?? { x: 0, y: 0 }),
  );
}

// The angles measured: each angle given, then the angle where the
// diagonals cross.
function anglesOf(spec: QuadDiagonalsSpec, points: PairPoint[]): number[] {
  const point = (index: number) => points[index] ?? { x: 0, y: 0 };
  return [
    ...spec.angles.map((angle) =>
      angleAt(point(angle.vertex), point(angle.arms[0]), point(angle.arms[1])),
    ),
    angleAt(point(CROSSING), point(0), point(1)),
  ];
}

function checkOf(spec: QuadDiagonalsSpec): Check {
  const labels = [...spec.sides, ...spec.diagonalLabels, ...spec.halves];
  const rights = spec.angles.flatMap((angle, index) =>
    angle.right ? [index] : [],
  );
  if (spec.rightAtCentre) rights.push(spec.angles.length);
  return {
    lengths: writtenLengths(labels)?.lengths ?? [],
    degrees: spec.angles.flatMap((angle, index) => {
      const size = degreesOf(angle.label);
      return size === null ? [] : [[index, size] as [number, number]];
    }),
    rights,
    equalParts: groupsOf([...spec.sideMarks, 0, 0, ...spec.halfMarks]),
    equalAngles: groupsOf(spec.angles.map((angle) => angle.arcs)),
  };
}

// How far, in degrees, two sides are from parallel.
function offParallel(points: PairPoint[], one: number, two: number): number {
  const way = (side: number) => {
    const from = points[side] ?? { x: 0, y: 0 };
    const to = points[(side + 1) % 4] ?? from;
    return Math.atan2(to.y - from.y, to.x - from.x) / RADIANS;
  };
  const off = Math.abs(way(one) - way(two)) % 180;
  return Math.min(off, 180 - off);
}

function arrowCost(spec: QuadDiagonalsSpec, points: PairPoint[]): number {
  let cost = 0;
  for (const group of groupsOf(spec.sideArrows))
    for (const other of group.slice(1))
      cost +=
        50 * (offParallel(points, group[0] ?? other, other) * RADIANS) ** 2;
  return cost;
}

function fitted(spec: QuadDiagonalsSpec): PairPoint[] | null {
  const check = checkOf(spec);
  const values = bestFit(fitRanges(spec.shape), (trial) => {
    const corners = outline(spec.shape, trial);
    if (!corners) return Infinity;
    const points = withCrossing(corners);
    return (
      checkCost(check, partsOf(points), anglesOf(spec, points)) +
      arrowCost(spec, points)
    );
  });
  const corners = outline(spec.shape, values);
  return corners ? withCrossing(corners) : null;
}

function verdictOf(spec: QuadDiagonalsSpec, points: PairPoint[]): Verdict {
  for (const group of groupsOf(spec.sideArrows))
    for (const other of group.slice(1))
      if (offParallel(points, group[0] ?? other, other) > 1.5) return 'bad';
  // No angle drawn too sharp to read.
  if (anglesOf(spec, points).some((angle) => angle < 8)) return 'bad';
  return judge(checkOf(spec), partsOf(points), anglesOf(spec, points));
}

// ---------------------------------------------------------------------------
// Reading.

function readAngle(
  value: unknown,
  letters: string[],
  diagonals: QuadDiagonalsSpec['diagonals'],
): QuadAngle | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null;
  const record = value as Record<string, unknown>;
  if (typeof record.angle !== 'string') return null;
  const name = record.angle.trim();
  if (name.length !== 3) return null;
  const at = name.split('').map((letter) => letters.indexOf(letter));
  const [first = -1, vertex = -1, second = -1] = at;
  if (at.some((index) => index < 0)) return null;
  // The lines from each corner: its two sides, and its diagonal when drawn
  // (to the far corner, or to the crossing point).
  const drawn = (corner: number) =>
    diagonals === 'both' || (diagonals === 'first') === (corner % 2 === 0);
  const reaches = (from: number, to: number): boolean => {
    if (from === CROSSING) return diagonals === 'both' && to !== CROSSING;
    if (to === CROSSING) return diagonals === 'both';
    const step = (to - from + 4) % 4;
    if (step === 1 || step === 3) return true;
    return step === 2 && drawn(from);
  };
  if (!reaches(vertex, first) || !reaches(vertex, second)) return null;
  // The two lines are not one line.
  const direction = (to: number) =>
    vertex !== CROSSING && to === CROSSING ? (vertex + 2) % 4 : to;
  const one = direction(first);
  const two = direction(second);
  if (one === two) return null;
  if (vertex === CROSSING && (one - two + 4) % 4 === 2) return null;
  const label = record.label === undefined ? '' : record.label;
  if (typeof label !== 'string' || !isAngleLabel(label.trim())) return null;
  const arcs = record.arcs === undefined ? 0 : record.arcs;
  const [count] = readCountList([arcs], 1) ?? [];
  if (count === undefined) return null;
  const right = record.right === undefined ? false : record.right;
  if (typeof right !== 'boolean' || (right && count > 0)) return null;
  if (label.trim() === '' && count === 0 && !right) return null;
  return {
    name,
    vertex,
    arms: [first, second],
    label: label.trim(),
    arcs: count,
    right,
  };
}

// The same angle, whichever way it is named.
function angleKey(angle: QuadAngle): string {
  const way = (to: number) =>
    angle.vertex !== CROSSING && to === CROSSING ? (angle.vertex + 2) % 4 : to;
  const arms = angle.arms.map(way).sort((a, b) => a - b);
  return `${String(angle.vertex)}:${arms.join(',')}`;
}

export function readQuadDiagonals(
  record: Record<string, unknown>,
): QuadDiagonalsSpec | null {
  const shape =
    record.shape === undefined
      ? 'quadrilateral'
      : QUAD_SHAPES.find((name) => name === record.shape);
  if (!shape) return null;
  const rawCorners =
    record.corners === undefined ? ['', '', '', ''] : record.corners;
  if (!Array.isArray(rawCorners) || rawCorners.length !== 4) return null;
  const corners: string[] = [];
  for (const entry of rawCorners as unknown[]) {
    if (typeof entry !== 'string') return null;
    const text = entry.trim();
    if (text !== '' && !CORNER_LETTER.test(text)) return null;
    corners.push(text);
  }
  const lettered = corners.every((letter) => letter !== '');
  if (!lettered && corners.some((letter) => letter !== '')) return null;
  const diagonals = record.diagonals === undefined ? 'both' : record.diagonals;
  if (diagonals !== 'both' && diagonals !== 'first' && diagonals !== 'second')
    return null;
  const both = diagonals === 'both';
  const centre = record.centre === undefined ? '' : record.centre;
  if (typeof centre !== 'string') return null;
  if (centre !== '' && (!CORNER_LETTER.test(centre) || !both || !lettered))
    return null;
  if (
    new Set([...corners, centre].filter((x) => x !== '')).size !==
    corners.filter((x) => x !== '').length + (centre === '' ? 0 : 1)
  )
    return null;
  const sides = readLabelList(record.sides, 4, isPartLabel);
  const sideMarks = readCountList(record.sideMarks, 4);
  const sideArrows = readCountList(record.sideArrows, 4);
  const diagonalLabels = readLabelList(record.diagonalLabels, 2, isPartLabel);
  const halves = readLabelList(record.halves, 4, isPartLabel);
  const halfMarks = readCountList(record.halfMarks, 4);
  if (
    !sides ||
    !sideMarks ||
    !sideArrows ||
    !diagonalLabels ||
    !halves ||
    !halfMarks
  )
    return null;
  // Marks and arrows are read out by the letters.
  if (!lettered && [...sideMarks, ...sideArrows].some((mark) => mark > 0))
    return null;
  // A diagonal that is not drawn carries no label.
  if (diagonals === 'first' && diagonalLabels[1] !== '') return null;
  if (diagonals === 'second' && diagonalLabels[0] !== '') return null;
  // The parts of the diagonals are named by the crossing point.
  if (
    centre === '' &&
    [...halves, ...halfMarks.map(String)].some((x) => x !== '' && x !== '0')
  )
    return null;
  const rightAtCentre =
    record.rightAtCentre === undefined ? false : record.rightAtCentre;
  if (typeof rightAtCentre !== 'boolean' || (rightAtCentre && !both))
    return null;
  const notToScale =
    record.notToScale === undefined ? false : record.notToScale;
  if (typeof notToScale !== 'boolean') return null;
  const angles: QuadAngle[] = [];
  if (record.angles !== undefined) {
    if (!Array.isArray(record.angles) || record.angles.length > MAX_ANGLES)
      return null;
    if (record.angles.length > 0 && !lettered) return null;
    const letters = [...corners, centre === '' ? '-' : centre];
    for (const entry of record.angles as unknown[]) {
      const angle = readAngle(entry, letters, diagonals);
      if (!angle) return null;
      angles.push(angle);
    }
    const keys = angles.map(angleKey);
    if (new Set(keys).size !== keys.length) return null;
    // The square where the diagonals cross is not drawn twice.
    if (
      rightAtCentre &&
      angles.some((angle) => angle.vertex === CROSSING && angle.right)
    )
      return null;
  }
  // The written lengths are all in one unit.
  if (!writtenLengths([...sides, ...diagonalLabels, ...halves])) return null;
  const spec: QuadDiagonalsSpec = {
    type: 'quad_diagonals',
    shape,
    corners,
    diagonals,
    centre,
    sides,
    sideMarks,
    sideArrows,
    diagonalLabels,
    halves,
    halfMarks,
    angles,
    rightAtCentre,
    notToScale,
  };
  const points = fitted(spec);
  if (!points) return null;
  const verdict = verdictOf(spec, points);
  if (verdict === 'bad') return null;
  if (verdict === 'untrue') spec.notToScale = true;
  return spec;
}

// ---------------------------------------------------------------------------
// What a screen reader says.

function partName(spec: QuadDiagonalsSpec, from: number, to: number): string {
  const letter = (index: number) =>
    index === CROSSING ? spec.centre : (spec.corners[index] ?? '');
  return `${letter(from)}${letter(to)}`;
}

// "AM and MC each have 1 small mark."
function marksSentences(
  names: string[],
  counts: number[],
  thing: string,
  prefix: [string, string],
): string[] {
  const words: string[] = [];
  for (let count = 1; count <= 3; count += 1) {
    const group = names.filter((_, index) => counts[index] === count);
    if (group.length === 0) continue;
    const [one, many] = prefix;
    words.push(
      group.length === 1
        ? `${one}${group[0] ?? ''} has ${marksWords(count, thing)}.`
        : `${many}${joinNames(group)} each have ${marksWords(count, thing)}.`,
    );
  }
  return words;
}

function sentence(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

export function describeQuadDiagonals(spec: QuadDiagonalsSpec): string {
  const lettered = spec.corners.every((letter) => letter !== '');
  const start = startsAtTop(spec.shape) ? 'the top' : 'the top left';
  const words = [
    lettered
      ? `A shape with 4 straight sides, corners ${spec.corners.join(', ')} going clockwise from ${start}.`
      : 'A shape with 4 straight sides.',
  ];
  const ac = partName(spec, 0, 2);
  const bd = partName(spec, 1, 3);
  if (spec.diagonals === 'both')
    words.push(
      !lettered
        ? 'Both of its diagonals are drawn.'
        : spec.centre !== ''
          ? `Its diagonals ${ac} and ${bd} cross at ${spec.centre}.`
          : `Its diagonals ${ac} and ${bd} are drawn.`,
    );
  else {
    const name = spec.diagonals === 'first' ? ac : bd;
    words.push(
      lettered ? `Its diagonal ${name} is drawn.` : 'One diagonal is drawn.',
    );
  }
  // The labels on the diagonals.
  const [first = '', second = ''] = spec.diagonalLabels;
  if (lettered) {
    if (first !== '') words.push(`Diagonal ${ac} ${labelWords(first)}.`);
    if (second !== '') words.push(`Diagonal ${bd} ${labelWords(second)}.`);
  } else {
    const given = [first, second].filter((label) => label !== '');
    if (given.length === 2)
      words.push(
        `The diagonals are labelled ${spokenList(given, 'diagonal')}.`,
      );
    else if (given.length === 1)
      words.push(
        `${spec.diagonals === 'both' ? 'One diagonal' : 'The diagonal'} ${labelWords(given[0] ?? '')}.`,
      );
  }
  const halfNames = PARTS.slice(6).map(([from, to]) =>
    partName(spec, from, to),
  );
  for (const [index, label] of spec.halves.entries())
    if (label !== '')
      words.push(`${halfNames[index] ?? ''} ${labelWords(label)}.`);
  const sideNames = PARTS.slice(0, 4).map(([from, to]) =>
    partName(spec, from, to),
  );
  if (lettered) {
    for (const [index, label] of spec.sides.entries())
      if (label !== '')
        words.push(`Side ${sideNames[index] ?? ''} ${labelWords(label)}.`);
  } else if (spec.sides.some((label) => label !== ''))
    words.push(
      `Its sides, going clockwise from the top, are labelled ${spokenList(spec.sides, 'side')}.`,
    );
  words.push(
    ...marksSentences(sideNames, spec.sideMarks, 'small mark', [
      'Side ',
      'Sides ',
    ]),
    ...marksSentences(sideNames, spec.sideArrows, 'arrow', ['Side ', 'Sides ']),
    ...marksSentences(halfNames, spec.halfMarks, 'small mark', ['', '']),
  );
  if (spec.rightAtCentre)
    words.push(
      'The angle where the diagonals cross is marked as a right angle.',
    );
  for (const angle of spec.angles) {
    const parts: string[] = [];
    if (angle.arcs > 0) parts.push(`has ${marksWords(angle.arcs, 'arc')}`);
    if (angle.right) parts.push('is marked as a right angle');
    if (angle.label !== '') parts.push(labelWords(angle.label));
    words.push(`Angle ${angle.name} ${parts.join(' and ')}.`);
  }
  if (spec.notToScale) words.push('Not drawn to scale.');
  return words.map(sentence).join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn.

export type QuadDiagonalsPicture = {
  outline: PairPoint[];
  diagonals: PairLine[];
  ticks: PairLine[];
  // Each arrow head as a bent line of three points.
  arrows: PairPoint[][];
  arcs: PairArc[];
  squares: PairPoint[][];
  // The crossing point, when it carries a letter.
  dot: PairPoint | null;
  letters: PairLabel[];
  sideLabels: PairLabel[];
  diagonalLabels: PairLabel[];
  halfLabels: PairLabel[];
  angleLabels: PairLabel[];
  note: PairLabel | null;
  box: Bounds;
};

// The largest the shape is drawn.
const WIDTH = 240;
const HEIGHT = 170;
const ARROW = 6;
const ARROW_SPACE = 6;
const PAD = 6;

// Arrow heads in the middle of a side, all pointing to the right (or down,
// for an upright side), so that sides with the same arrows point alike.
function arrowsOn(line: PairLine, count: number): PairPoint[][] {
  let way = unit(line.from, line.to);
  if (way.x < -1e-6 || (Math.abs(way.x) <= 1e-6 && way.y < 0))
    way = { x: -way.x, y: -way.y };
  const across = { x: -way.y, y: way.x };
  const middle = between(line, 0.5);
  return Array.from({ length: count }, (_, index) => {
    const tip = along(
      middle,
      way,
      (index - (count - 1) / 2) * ARROW_SPACE + ARROW / 2,
    );
    const back = along(tip, way, -ARROW);
    return [
      along(back, across, ARROW * 0.7),
      tip,
      along(back, across, -ARROW * 0.7),
    ];
  });
}

const pictures = new WeakMap<QuadDiagonalsSpec, QuadDiagonalsPicture>();

export function quadDiagonalsPicture(
  spec: QuadDiagonalsSpec,
): QuadDiagonalsPicture {
  const known = pictures.get(spec);
  if (known) return known;
  const raw = fitted(spec) ?? withCrossing(outline('quadrilateral', []) ?? []);
  const xs = raw.map((point) => point.x);
  const ys = raw.map((point) => point.y);
  const scale = Math.min(
    WIDTH / (Math.max(...xs) - Math.min(...xs) || 1),
    HEIGHT / (Math.max(...ys) - Math.min(...ys) || 1),
  );
  const points = raw.map((point) => ({
    x: point.x * scale,
    y: point.y * scale,
  }));
  const point = (index: number) => points[index] ?? { x: 0, y: 0 };
  const corners = points.slice(0, 4);
  const centre = point(CROSSING);
  const sides = sidesOf(corners);
  const diagonals: PairLine[] = [];
  if (spec.diagonals !== 'second')
    diagonals.push({ from: point(0), to: point(2) });
  if (spec.diagonals !== 'first')
    diagonals.push({ from: point(1), to: point(3) });
  const halves = PARTS.slice(6).map(([from, to]) => ({
    from: point(from),
    to: point(to),
  }));
  const ticks = [
    ...sides.flatMap((line, index) =>
      ticksOn(line, spec.sideMarks[index] ?? 0),
    ),
    ...halves.flatMap((line, index) =>
      ticksOn(line, spec.halfMarks[index] ?? 0),
    ),
  ];
  const arrows = sides.flatMap((line, index) =>
    arrowsOn(line, spec.sideArrows[index] ?? 0),
  );
  const arrowLines = arrows.flatMap(([a, b, c]) =>
    a && b && c
      ? [
          { from: a, to: b },
          { from: b, to: c },
        ]
      : [],
  );
  const lines = [...sides, ...diagonals, ...ticks, ...arrowLines];
  const letters = spec.corners.flatMap((letter, index) =>
    letter === ''
      ? []
      : [
          letterOutside(
            point(index),
            point(index + 3),
            point((index + 1) % 4),
            letter,
          ),
        ],
  );
  const arcs: PairArc[] = [];
  const squares: PairPoint[][] = [];
  const angleLabels: PairLabel[] = [];
  // The four angles where the diagonals cross, by the corners on their
  // arms: AMB, BMC, CMD, DMA.
  const used = new Set<number>();
  for (const angle of spec.angles) {
    const vertex = point(angle.vertex);
    const [one, two] = angle.arms.map(point) as [PairPoint, PairPoint];
    if (angle.vertex === CROSSING) {
      const [p, q] = angle.arms;
      used.add((q - p + 4) % 4 === 1 ? p : q);
    }
    arcs.push(...arcsAt(vertex, one, two, angle.arcs));
    if (angle.right) squares.push(squareAt(vertex, one, two));
    if (angle.label !== '')
      angleLabels.push(
        cornerLabel(vertex, one, two, angle.label, angle.arcs, angle.right),
      );
  }
  // The square where the diagonals cross sits in a free angle, the lower
  // right one first.
  if (spec.rightAtCentre) {
    const sector = [1, 0, 2, 3].find((index) => !used.has(index)) ?? 1;
    used.add(sector);
    squares.push(squareAt(centre, point(sector), point((sector + 1) % 4)));
  }
  const boxes = (): PairLabel[] => [
    ...letters,
    ...angleLabels,
    // Each square, as a box to keep labels off.
    ...squares.map((square) => {
      const bounds = boundsOf(square, []);
      return {
        x: bounds.x + bounds.width / 2,
        y: bounds.y + bounds.height / 2,
        text: '',
        width: bounds.width,
        height: bounds.height,
      };
    }),
  ];
  // The crossing point's letter, in a free angle, the left one first.
  let dot: PairPoint | null = null;
  if (spec.centre !== '') {
    dot = centre;
    const spots = [3, 0, 2, 1]
      .filter((sector) => !used.has(sector))
      .map((sector) => {
        const placed = letterOutside(
          centre,
          point(sector),
          point((sector + 1) % 4),
          spec.centre,
        );
        // letterOutside points away from the angle; turn it into it.
        return {
          ...placed,
          x: 2 * centre.x - placed.x,
          y: 2 * centre.y - placed.y,
        };
      });
    const spot = firstClear(spots, lines, boxes());
    if (spot) letters.push(spot);
  }
  const clockwise = turning(corners) > 0;
  const sideLabels: PairLabel[] = [];
  const diagonalLabels: PairLabel[] = [];
  const halfLabels: PairLabel[] = [];
  const placed = () => [
    ...boxes(),
    ...sideLabels,
    ...diagonalLabels,
    ...halfLabels,
  ];
  for (const [index, line] of sides.entries()) {
    const text = spec.sides[index] ?? '';
    if (text === '') continue;
    const spots = spotsBeside(line, outwardOf(line, clockwise), text, PLACES);
    const spot = firstClear(spots, lines, placed());
    if (spot) sideLabels.push(spot);
  }
  // Beside a line, on either side of it.
  const besideEither = (line: PairLine, text: string, parts: number[]) => {
    const way = unit(line.from, line.to);
    const normal = { x: -way.y, y: way.x };
    return parts.flatMap((part) => [
      ...spotsBeside(line, normal, text, [part]),
      ...spotsBeside(line, { x: -normal.x, y: -normal.y }, text, [part]),
    ]);
  };
  // Beside the middle of its own line, just off it, on the side with more
  // room, and always nearer its own line than any other: so that a child can
  // tell which line it labels. Null when no spot keeps all that.
  const besideOwn = (
    line: PairLine,
    text: string,
    parts: number[],
    middle: number,
    others: PairLine[],
  ): PairLabel | null => {
    const way = unit(line.from, line.to);
    const width = labelWidth(text);
    let best: PairLabel | null = null;
    let score = -Infinity;
    for (const part of parts)
      for (const sign of [1, -1])
        for (const gap of [GAP, 5]) {
          const normal = { x: -way.y * sign, y: way.x * sign };
          const reach =
            (Math.abs(normal.x) * width + Math.abs(normal.y) * LABEL_HEIGHT) /
            2;
          const spot = {
            ...along(between(line, part), normal, gap + reach),
            text,
            width,
            height: LABEL_HEIGHT,
          };
          if (lines.some((other) => crosses(spot, other, 1.5))) continue;
          if (placed().some((other) => overlaps(spot, other, 3))) continue;
          const own = distanceToLine(spot, line);
          const room = Math.min(
            ...others.map((other) => distanceToLine(spot, other)),
          );
          if (own + 1 > room) continue;
          const value = room - own - 60 * Math.abs(part - middle);
          if (value > score) {
            score = value;
            best = spot;
          }
        }
    return best;
  };
  for (const [index, text] of spec.diagonalLabels.entries()) {
    if (text === '') continue;
    const line = { from: point(index), to: point(index + 2) };
    // With both drawn, the label sits along the longer part, away from the
    // crossing, so that it is seen to belong to its own diagonal.
    const cross = distance(line.from, centre) / distance(line.from, line.to);
    const longer = cross < 0.5 ? (cross + 1) / 2 : cross / 2;
    const shorter = cross < 0.5 ? cross / 2 : (cross + 1) / 2;
    const parts =
      spec.diagonals === 'both'
        ? [
            0, -0.035, 0.035, -0.07, 0.07, -0.105, 0.105, -0.14, 0.14, -0.175,
            0.175, -0.21, 0.21,
          ]
            .flatMap((step) => [longer + step, shorter + step])
            .filter((part) => part > 0.1 && part < 0.9)
        : [0.5, 0.4, 0.6, 0.3, 0.7];
    const other = diagonals.filter(
      (diagonal) => diagonal.from !== line.from && diagonal.to !== line.from,
    );
    const spot =
      besideOwn(line, text, parts, spec.diagonals === 'both' ? longer : 0.5, [
        ...sides,
        ...other,
      ]) ??
      clearestAway(besideEither(line, text, parts), lines, placed(), [
        ...sides,
        ...other,
      ]);
    if (spot) diagonalLabels.push(spot);
  }
  for (const [index, text] of spec.halves.entries()) {
    if (text === '') continue;
    const line = halves[index];
    if (!line) continue;
    // The other diagonal; this half's own diagonal runs on in a straight
    // line, so it is not another line.
    const other = diagonals[index < 2 ? 1 : 0];
    const parts = [0.5, 0.44, 0.56, 0.38, 0.62, 0.32, 0.68, 0.26, 0.74];
    const spot =
      besideOwn(line, text, parts, 0.5, other ? [...sides, other] : sides) ??
      clearestAway(besideEither(line, text, parts), lines, placed(), sides);
    if (spot) halfLabels.push(spot);
  }
  const labels = [
    ...letters,
    ...sideLabels,
    ...diagonalLabels,
    ...halfLabels,
    ...angleLabels,
  ];
  const drawn = boundsOf(corners, labels);
  const note = spec.notToScale
    ? {
        x: drawn.x + drawn.width / 2,
        y: drawn.y + drawn.height + 8 + LABEL_HEIGHT / 2,
        text: NOT_TO_SCALE,
        width: NOT_TO_SCALE.length * 6.4,
        height: LABEL_HEIGHT,
      }
    : null;
  const bounds = boundsOf(corners, note ? [...labels, note] : labels);
  const picture: QuadDiagonalsPicture = {
    outline: corners,
    diagonals,
    ticks,
    arrows,
    arcs,
    squares,
    dot,
    letters,
    sideLabels,
    diagonalLabels,
    halfLabels,
    angleLabels,
    note,
    box: {
      x: bounds.x - PAD,
      y: bounds.y - PAD,
      width: bounds.width + 2 * PAD,
      height: bounds.height + 2 * PAD,
    },
  };
  pictures.set(spec, picture);
  return picture;
}
